import { CommonModule } from '@angular/common';
import { EAbaConsultorioPrivado } from '@/features/servicos/shared/enums/abas-servicos.enum';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';

import type { ConsultorioPrivadoDTO, ConsultorioPrivadoResponse } from '@/core/api/sicom-api/servicos/pessoa-fisica/consultorio-privado/consultorio-privado.model';
import { ConsultorioPrivadoServicosService } from '@/features/servicos/pessoa-fisica/content/consultorio-privado-servicos/consultorio-privado-servicos.service';
import { formatarDataHoraMascara, formatarDataMascara, montarRequestProtocolo, valorTexto, type ProtocoloResumoConsulta } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';
import { rotuloBadgeSituacao, situacaoClassePorTexto } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface CampoVisualizacao {
    label: string;
    chave?: keyof ConsultorioPrivadoDTO;
    resolvedor?: (dados: ConsultorioPrivadoDTO) => unknown;
    formatador?: (valor: unknown) => string;
}

interface SecaoVisualizacao {
    titulo: string;
    campos: CampoVisualizacao[];
}

interface VisibilidadeAbasConsultorio {
    mostrarCompartilhamento: boolean;
    mostrarEspecialidades: boolean;
}

/** Linha da tabela de telefones do consultório (valores já formatados para exibição). */
interface TelefoneConsultorio {
    tipo: string;
    ddd: string;
    telefone: string;
    ramal: string;
    observacao: string;
}

/** Linha da tabela de médicos que compartilham o consultório. */
interface CompartilhamentoConsultorio {
    uf: string;
    nome: string;
    entrada: string;
    saida: string;
}

/** Linha da tabela de especialidades/áreas de atuação do consultório. */
interface EspecialidadeConsultorio {
    especialidade: string;
    areaAtuacao: string;
    dataInicio: string;
    dataFim: string;
}

/** Linha da tabela de horários de funcionamento do consultório. */
interface HorarioConsultorio {
    diaSemana: string;
    manhaInicio: string;
    manhaFim: string;
    tardeInicio: string;
    tardeFim: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-consultorio-privado-cadastro-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, CardContent, DialogModule, VisualizadorPdf, TableModule, InputTextModule],
    templateUrl: './consultorio-privado-cadastro-servicos.html',
    styleUrl: './consultorio-privado-cadastro-servicos.scss'
})
export class ConsultorioPrivadoCadastroServicos {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;
    protected readonly situacaoClassePorTexto = situacaoClassePorTexto;
    protected readonly rotuloBadgeSituacao = rotuloBadgeSituacao;

    protected readonly EAbaConsultorioPrivado = EAbaConsultorioPrivado;
    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');
    readonly abaSelecionadaExterna = input<EAbaConsultorioPrivado | null>(null);
    readonly visibilidadeAbasAlterada = output<VisibilidadeAbasConsultorio>();

    readonly carregando = signal(false);
    readonly erroConsulta = signal<string | null>(null);
    readonly dados = signal<ConsultorioPrivadoResponse | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo.pdf');

    readonly abaSelecionada = signal<EAbaConsultorioPrivado>(EAbaConsultorioPrivado.DADOS);
    readonly mostrarAbaCompartilhamento = signal(true);
    readonly mostrarAbaEspecialidades = signal(true);

    telefoneTipoOptions = [
        { label: 'FIXO', value: 'F' },
        { label: 'CELULAR', value: 'C' }
    ];

    readonly telefones = signal<TelefoneConsultorio[]>([]);
    readonly compartilhamentos = signal<CompartilhamentoConsultorio[]>([]);
    readonly especialidades = signal<EspecialidadeConsultorio[]>([]);
    readonly horariosFuncionamento = signal<HorarioConsultorio[]>([]);

    readonly secoes: SecaoVisualizacao[] = [
        {
            titulo: 'Protocolo',
            campos: [
                { label: 'Número Protocolo', chave: 'numeroProtocolo' },
                { label: 'Ano Protocolo', chave: 'anoProtocolo' },
                { label: 'Data/Hora Solicitacao', chave: 'dataHora', formatador: formatarDataHoraMascara },
                { label: 'Situação', chave: 'situacao', formatador: this.formatarSituacaoProtocolo }
            ]
        },
        {
            titulo: 'Dados do Solicitante',
            campos: [
                { label: 'Nome', chave: 'nome' },
                { label: 'CPF', chave: 'cpf' },
                { label: 'CRM', chave: 'crm' },
                { label: 'UF', chave: 'uf' },
                { label: 'Email Comunicação', chave: 'emailComunicacao' }
            ]
        },
        {
            titulo: 'Dados do Consultório',
            campos: [
                { label: 'CNES', chave: 'cnes' },
                { label: 'Data Registro', chave: 'dtRegistro', formatador: formatarDataMascara },
                { label: 'Data Atualização', chave: 'dtAtualizacao', formatador: formatarDataMascara },
                { label: 'Uso', chave: 'inUso', formatador: this.formatarUso },
                { label: 'Especializado', chave: 'inEspecializado', formatador: this.formatarSimNao },
                { label: 'Situação do Consultório', chave: 'inSituacao' }
            ]
        },
        {
            titulo: 'Endereço',
            campos: [
                { label: 'CEP', chave: 'cep' },
                { label: 'Logradouro', chave: 'logradouro' },
                { label: 'Bairro', chave: 'bairro' },
                { label: 'Município', resolvedor: (dados) => dados.municipio?.descricao ?? '' },
                { label: 'UF Município', resolvedor: (dados) => dados.municipio?.uf ?? '' },
                { label: 'Permite Divulgação', chave: 'inDivulga', formatador: this.formatarSimNao }
            ]
        },
        {
            titulo: 'Licenças e Alvará',
            campos: [
                { label: 'Registro Sanitário', chave: 'registroSanitario' },
                { label: 'Tipo Registro Sanitário', chave: 'inTipoRegSanitario', formatador: this.formatarTipoRegistro },
                { label: 'Validade Registro Sanitário', chave: 'dtValidadeRegSanitario', formatador: formatarDataMascara },
                { label: 'Isenção Registro Sanitário', chave: 'inIsencaoRegistroSanitario', formatador: this.formatarSimNao },
                { label: 'Número Alvará', chave: 'alvara' },
                { label: 'Validade Alvará', chave: 'dtValidadeAlvara', formatador: formatarDataMascara },
                { label: 'Isenção Alvará', chave: 'inIsencaoAlvara', formatador: this.formatarSimNao }
            ]
        },
        {
            titulo: 'Resumo de Estruturas',
            campos: [
                { label: 'Telefones', resolvedor: (dados) => dados.telefones?.length ?? 0, formatador: this.formatarContagem },
                { label: 'Médicos Compartilhados', resolvedor: (dados) => dados.medicos?.length ?? 0, formatador: this.formatarContagem },
                { label: 'Especialidades', resolvedor: (dados) => dados.especialidades?.length ?? 0, formatador: this.formatarContagem },
                { label: 'Horários', resolvedor: (dados) => dados.horarios?.length ?? 0, formatador: this.formatarContagem }
            ]
        }
    ];

    get secoesDados(): SecaoVisualizacao[] {
        return this.secoes.filter((secao) => secao.titulo !== 'Endereço');
    }

    get secaoEndereco(): SecaoVisualizacao | undefined {
        return this.secoes.find((secao) => secao.titulo === 'Endereço');
    }

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';

    private readonly servico = inject(ConsultorioPrivadoServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        effect(() => {
            this.abaSelecionadaExterna();
            this.sincronizarAbaExterna();
        });
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    getTelefoneTipoLabel(value: string): string {
        const opt = this.telefoneTipoOptions.find((o) => o.value === value);
        return opt?.label ?? value;
    }

    valorCampo(campo: CampoVisualizacao): string {
        const dados = this.dados();
        if (!dados) {
            return '-';
        }

        const valorBruto = campo.resolvedor ? campo.resolvedor(dados) : campo.chave ? dados[campo.chave] : undefined;
        if (valorBruto === null || valorBruto === undefined || valorTexto(valorBruto).trim() === '') {
            return '-';
        }

        if (campo.formatador) {
            const valorFormatado = campo.formatador(valorBruto);
            return valorFormatado ? valorFormatado : '-';
        }

        return valorTexto(valorBruto);
    }

    /**
     * Id do controle de um campo, para o `<label for>` apontar para ele.
     *
     * Os campos vêm de configuração e se repetem entre seções ("UF" está em Solicitante e em
     * Endereço), então o id junta seção e rótulo — só o rótulo geraria ids duplicados, e o clique no
     * label levaria o foco para o campo da outra seção.
     */
    idDoCampo(secao: SecaoVisualizacao, campo: CampoVisualizacao): string {
        return `consultorio-${this.emSlug(secao.titulo)}-${this.emSlug(campo.label)}`;
    }

    /** Texto reduzido a `a-z0-9-`, para servir de id de elemento. */
    private emSlug(texto: string): string {
        return texto
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    }

    ehCampoBadge(campo: CampoVisualizacao): boolean {
        const label = campo.label.toLowerCase();
        return campo.chave === 'situacao' || label.includes('situacao') || label.includes('situa') || label.includes('uso') || label.includes('especializado') || label.includes('isencao') || label.includes('permite');
    }

    possuiAnexoAlvara(): boolean {
        return !!valorTexto(this.dados()?.urlAnexoAlvara).trim();
    }

    possuiAnexoRegistroSanitario(): boolean {
        return !!valorTexto(this.dados()?.urlAnexoRegistroSanitario).trim();
    }

    onVisualizarAnexoAlvara(event: Event): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(this.dados()?.urlAnexoAlvara, 'anexo-alvara.pdf');
    }

    onVisualizarAnexoRegistroSanitario(event: Event): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(this.dados()?.urlAnexoRegistroSanitario, 'anexo-registro-sanitario.pdf');
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private sincronizarAbaExterna(): void {
        const aba = this.abaSelecionadaExterna();
        if (!aba) {
            return;
        }

        if (!this.abaPermitida(aba)) {
            this.abaSelecionada.set(EAbaConsultorioPrivado.DADOS);
            return;
        }

        this.abaSelecionada.set(aba);
    }

    private abaPermitida(aba: EAbaConsultorioPrivado): boolean {
        if (aba === EAbaConsultorioPrivado.COMP && !this.mostrarAbaCompartilhamento()) {
            return false;
        }

        if (aba === EAbaConsultorioPrivado.ESP && !this.mostrarAbaEspecialidades()) {
            return false;
        }

        return true;
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dados.set(null);
            this.carregando.set(false);
            this.erroConsulta.set(null);
            this.limparDados();
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;
        this.carregando.set(true);
        this.erroConsulta.set(null);

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dados.set(response ?? null);
                    this.carregando.set(false);

                    const dadosAtual = this.dados();
                    if (dadosAtual) {
                        this.atualizarVisibilidadeAbas(dadosAtual);
                        this.preencherTabelas(dadosAtual);
                    }
                },
                error: () => {
                    this.dados.set(null);
                    this.carregando.set(false);
                    this.erroConsulta.set('Falha ao carregar os dados de consultório privado cadastro.');
                    this.limparDados();
                }
            });
    }

    private limparDados(): void {
        this.mostrarAbaCompartilhamento.set(true);
        this.mostrarAbaEspecialidades.set(true);
        this.visibilidadeAbasAlterada.emit({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
        this.telefones.set([]);
        this.compartilhamentos.set([]);
        this.especialidades.set([]);
        this.horariosFuncionamento.set([]);
    }

    private atualizarVisibilidadeAbas(response: ConsultorioPrivadoResponse): void {
        this.mostrarAbaCompartilhamento.set(this.deveMostrarAbaCompartilhamento(response.inUso));
        this.mostrarAbaEspecialidades.set(this.deveMostrarAbaEspecialidades(response.inEspecializado));
        this.visibilidadeAbasAlterada.emit({
            mostrarCompartilhamento: this.mostrarAbaCompartilhamento(),
            mostrarEspecialidades: this.mostrarAbaEspecialidades()
        });

        if (!this.abaPermitida(this.abaSelecionada())) {
            this.abaSelecionada.set(EAbaConsultorioPrivado.DADOS);
        }
    }

    private deveMostrarAbaCompartilhamento(uso: unknown): boolean {
        const valorUso = valorTexto(uso).trim().toUpperCase();
        return valorUso !== 'E' && valorUso !== 'EXCLUSIVO';
    }

    private deveMostrarAbaEspecialidades(especializado: unknown): boolean {
        const valorEspecializado = valorTexto(especializado).trim().toUpperCase();
        if (['N', 'NAO', 'NÃO', 'FALSE', '0'].includes(valorEspecializado)) {
            return false;
        }

        return true;
    }

    private preencherTabelas(response: ConsultorioPrivadoResponse): void {
        this.telefones.set(
            response.telefones?.map((telefone) => ({
                tipo: valorTexto(telefone.tipoTelefone),
                ddd: valorTexto(telefone.ddd),
                telefone: valorTexto(telefone.telefone),
                ramal: valorTexto(telefone.ramal),
                observacao: valorTexto(telefone.observacao)
            })) ?? []
        );

        this.compartilhamentos.set(
            response.medicos?.map((medico) => ({
                uf: valorTexto(medico.uf),
                nome: valorTexto(medico.nome),
                entrada: formatarDataMascara(medico.dtEntrada),
                saida: formatarDataMascara(medico.dtFim)
            })) ?? []
        );

        this.especialidades.set(
            response.especialidades?.map((especialidade) => ({
                especialidade: valorTexto(especialidade.especialidade?.descricao),
                areaAtuacao: valorTexto(especialidade.areaAtuacao?.descricao),
                dataInicio: formatarDataMascara(especialidade.dtInclusao),
                dataFim: formatarDataMascara(especialidade.dtFim)
            })) ?? []
        );

        this.horariosFuncionamento.set(
            response.horarios?.map((horario) => ({
                diaSemana: valorTexto(horario.diaSemanaInicio?.descricao || horario.diaSemanaFim?.descricao),
                manhaInicio: valorTexto(horario.horaManhaInicio),
                manhaFim: valorTexto(horario.horaManhaFim),
                tardeInicio: valorTexto(horario.horaTardeInicio),
                tardeFim: valorTexto(horario.horaTardeFim)
            })) ?? []
        );
    }

    private formatarSituacaoProtocolo(valor: unknown): string {
        const codigo = valorTexto(valor).trim().toUpperCase();
        switch (codigo) {
            case 'A':
                return 'Ativo';
            case 'T':
                return 'Tramitado';
            case 'P':
                return 'Pendência';
            case 'C':
                return 'Cancelado';
            case 'E':
                return 'Encerrado';
            default:
                return valorTexto(valor);
        }
    }

    private formatarUso(valor: unknown): string {
        const texto = valorTexto(valor).trim().toUpperCase();
        switch (texto) {
            case 'E':
            case 'EXCLUSIVO':
                return 'Exclusivo';
            case 'C':
            case 'COMPARTILHADO':
                return 'Compartilhado';
            default:
                return valorTexto(valor);
        }
    }

    private formatarTipoRegistro(valor: unknown): string {
        const texto = valorTexto(valor).trim().toUpperCase();
        switch (texto) {
            case 'M':
            case 'MUNICIPAL':
                return 'Municipal';
            case 'E':
            case 'ESTADUAL':
                return 'Estadual';
            default:
                return valorTexto(valor);
        }
    }

    private formatarSimNao(valor: unknown): string {
        const texto = valorTexto(valor).trim().toUpperCase();
        if (!texto) {
            return '';
        }

        if (['S', 'SIM', 'TRUE', '1'].includes(texto)) {
            return 'Sim';
        }

        if (['N', 'NAO', 'NÃO', 'FALSE', '0'].includes(texto)) {
            return 'Não';
        }

        return valorTexto(valor);
    }

    private formatarContagem(valor: unknown): string {
        const numero = Number(valor);
        if (Number.isFinite(numero)) {
            return `${numero}`;
        }

        return valorTexto(valor);
    }

    private visualizarAnexoPorCampo(campoAnexo: unknown, nomeArquivo: string): void {
        const idAnexo = this.extrairIdAnexo(campoAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.nomeArquivoAnexo.set(nomeArquivo);
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const texto = valorTexto(campoAnexo).trim();
        if (!texto) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(texto);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(texto, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e segue fallback.
        }

        if (/^[A-Za-z0-9_-]+$/.test(texto)) {
            return texto;
        }

        return null;
    }
}
